import React, { useState, useRef, useEffect } from 'react';
import { Link } from '@inertiajs/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { motion, AnimatePresence } from 'motion/react';

interface WorkflowCard {
  function_name: string;
  display_name: string;
  tagline: string;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  options?: string[];
  workflow_card?: WorkflowCard;
  run_url?: string;
  error?: boolean;
}

interface Context {
  run_id?: string;
  workflow_id?: string;
}

interface ChatSession {
  id: string;
  title?: string;
  messages: Message[];
  context: Context;
}

interface Category {
  name: string;
  description: string;
  workflows: Array<{
    function_name: string;
    display_name: string;
    tagline: string;
  }>;
}

interface RecentChat {
  id: string;
  title: string;
  updatedAt: string;
}

interface Props {
  recommendations: Category[];
  starters: string[];
  user: {
    email: string;
    name: string;
  };
  chatSession?: ChatSession;
  recentChats: RecentChat[];
}

export default function ChatIndex({
  recommendations: _recommendations,
  starters,
  user: _user,
  chatSession,
  recentChats,
}: Props) {
  const [messages, setMessages] = useState<Message[]>(chatSession?.messages || []);
  const [context, setContext] = useState<Context>(chatSession?.context || {});
  const [chatSessionId, setChatSessionId] = useState<string | undefined>(chatSession?.id);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Auto-resize textarea
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 200) + 'px';
    }
  }, [input]);

  const sendMessage = async (text: string) => {
    if (!text.trim() || loading) return;

    const userMsg: Message = { role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch('/chat/message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token':
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
        },
        body: JSON.stringify({
          messages: newMessages.map((m) => ({ role: m.role, content: m.content })),
          context,
          chat_session_id: chatSessionId,
        }),
      });

      const data = await res.json();

      if (data.chat_session_id && !chatSessionId) {
        setChatSessionId(data.chat_session_id);
        window.history.pushState({}, '', `/chat/${data.chat_session_id}`);
      }

      if (data.context) {
        setContext((prev) => ({ ...prev, ...data.context }));
      }

      const assistantMsg: Message = {
        role: 'assistant',
        content: data.message || 'I received your message but had trouble generating a response.',
        options: data.options,
        workflow_card: data.workflow_card,
        run_url: data.action_result?.url,
        error: data.error,
      };

      setMessages([...newMessages, assistantMsg]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages([
        ...newMessages,
        {
          role: 'assistant',
          content: 'Sorry, I encountered an error connecting to the server.',
          error: true,
        },
      ]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void sendMessage(input);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void sendMessage(input);
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setContext({});
    setChatSessionId(undefined);
    window.history.pushState({}, '', '/chat');
    setHistoryOpen(false);
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative flex h-screen overflow-hidden bg-gradient-to-br from-zinc-50 via-white to-indigo-50/40 dark:from-zinc-950 dark:via-zinc-900 dark:to-indigo-950/30">
      {/* Subtle background pattern */}
      <div
        className="absolute inset-0 opacity-[0.015] dark:opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      {/* History Sidebar */}
      <AnimatePresence>
        {historyOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setHistoryOpen(false)}
              className="absolute inset-0 bg-black/20 backdrop-blur-sm z-40 lg:hidden"
            />
            {/* Sidebar */}
            <motion.aside
              initial={{ x: -320, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -320, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="absolute lg:relative z-50 w-80 h-full bg-white/80 dark:bg-zinc-900/80 backdrop-blur-xl border-r border-zinc-200/50 dark:border-zinc-800/50 flex flex-col"
            >
              <div className="p-4 border-b border-zinc-200/50 dark:border-zinc-800/50">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 tracking-tight">
                    Chat History
                  </h2>
                  <button
                    onClick={() => setHistoryOpen(false)}
                    className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-2">
                <button
                  onClick={startNewChat}
                  className="w-full mb-2 px-3 py-2.5 flex items-center gap-3 rounded-xl text-sm font-medium
                           bg-gradient-to-r from-indigo-500 to-violet-500 text-white
                           hover:from-indigo-600 hover:to-violet-600 transition-all shadow-lg shadow-indigo-500/25"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  New Conversation
                </button>

                {recentChats.length > 0 && (
                  <div className="space-y-1">
                    {recentChats.map((chat, index) => (
                      <motion.div
                        key={chat.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Link
                          href={`/chat/${chat.id}`}
                          onClick={() => setHistoryOpen(false)}
                          className={`block px-3 py-2.5 rounded-xl text-sm transition-all group
                                    ${
                                      chat.id === chatSessionId
                                        ? 'bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200/50 dark:border-indigo-800/50'
                                        : 'hover:bg-zinc-100 dark:hover:bg-zinc-800/50'
                                    }`}
                        >
                          <div
                            className={`font-medium truncate ${
                              chat.id === chatSessionId
                                ? 'text-indigo-700 dark:text-indigo-300'
                                : 'text-zinc-700 dark:text-zinc-300'
                            }`}
                          >
                            {chat.title || 'Untitled chat'}
                          </div>
                          <div className="text-xs text-zinc-400 dark:text-zinc-500 mt-0.5">
                            {formatRelativeTime(chat.updatedAt)}
                          </div>
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                )}

                {recentChats.length === 0 && (
                  <div className="text-center py-8 text-zinc-400 dark:text-zinc-500 text-sm">
                    No previous chats yet
                  </div>
                )}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Chat Area */}
      <div className="relative flex-1 flex flex-col max-w-4xl mx-auto w-full">
        {/* Header */}
        <header className="relative z-10 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setHistoryOpen(true)}
              className="p-2 rounded-xl text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200
                       hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
            <div>
              <h1 className="text-base font-semibold text-zinc-900 dark:text-zinc-100 tracking-tight">
                Workflow Assistant
              </h1>
            </div>
          </div>

          {messages.length > 0 && (
            <button
              onClick={startNewChat}
              className="px-3 py-1.5 text-sm rounded-lg font-medium
                       text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20
                       transition-colors"
            >
              New Chat
            </button>
          )}
        </header>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4">
          <div className="max-w-3xl mx-auto py-4 space-y-6">
            {messages.length === 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-center py-16"
              >
                {/* Animated icon */}
                <motion.div
                  className="relative w-20 h-20 mx-auto mb-6"
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring' }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-400 to-violet-500 rounded-2xl rotate-6 opacity-20" />
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-2xl flex items-center justify-center">
                    <svg
                      className="w-10 h-10 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  </div>
                </motion.div>

                <h2 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100 mb-2 tracking-tight">
                  What would you like to create?
                </h2>
                <p className="text-zinc-500 dark:text-zinc-400 mb-8 max-w-md mx-auto">
                  I&apos;ll help you find and run the perfect workflow for content creation,
                  research, and more.
                </p>

                <div className="flex flex-wrap gap-2 justify-center max-w-xl mx-auto">
                  {starters.map((starter, index) => (
                    <motion.button
                      key={starter}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 + index * 0.1 }}
                      onClick={() => void sendMessage(starter)}
                      className="px-4 py-2.5 text-sm rounded-xl font-medium
                               bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700
                               text-zinc-700 dark:text-zinc-300
                               hover:border-indigo-300 dark:hover:border-indigo-700
                               hover:bg-indigo-50 dark:hover:bg-indigo-900/20
                               hover:text-indigo-700 dark:hover:text-indigo-300
                               shadow-sm hover:shadow-md transition-all duration-200"
                    >
                      {starter}
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            )}

            <AnimatePresence mode="popLayout">
              {messages.map((msg, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`relative max-w-[85%] ${msg.role === 'user' ? 'order-1' : 'order-1'}`}
                  >
                    {/* Avatar indicator */}
                    {msg.role === 'assistant' && (
                      <div className="absolute -left-10 top-0 w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/25">
                        <svg
                          className="w-4 h-4 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 10V3L4 14h7v7l9-11h-7z"
                          />
                        </svg>
                      </div>
                    )}

                    <div
                      className={`rounded-2xl px-4 py-3 ${
                        msg.role === 'user'
                          ? 'bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-lg shadow-indigo-500/25'
                          : msg.error
                            ? 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800/50'
                            : 'bg-white dark:bg-zinc-800/80 text-zinc-900 dark:text-zinc-100 shadow-sm border border-zinc-100 dark:border-zinc-700/50'
                      }`}
                    >
                      {/* Message content */}
                      <div
                        className={`prose prose-sm max-w-none ${
                          msg.role === 'user'
                            ? 'prose-invert'
                            : 'dark:prose-invert prose-indigo prose-headings:font-semibold prose-headings:tracking-tight'
                        }`}
                      >
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                      </div>

                      {/* Workflow card */}
                      {msg.workflow_card && (
                        <Link
                          href={`/catalog/flow/${msg.workflow_card.function_name}`}
                          className="mt-4 block p-4 bg-gradient-to-br from-zinc-50 to-indigo-50/50 dark:from-zinc-900 dark:to-indigo-900/20
                                   rounded-xl border border-zinc-200/50 dark:border-zinc-700/50
                                   hover:border-indigo-300 dark:hover:border-indigo-700
                                   hover:shadow-lg hover:shadow-indigo-500/10
                                   transition-all duration-200 no-underline group"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="font-semibold text-zinc-900 dark:text-zinc-100 group-hover:text-indigo-700 dark:group-hover:text-indigo-300 transition-colors">
                                {msg.workflow_card.display_name}
                              </div>
                              <div className="text-sm text-zinc-500 dark:text-zinc-400 mt-0.5">
                                {msg.workflow_card.tagline}
                              </div>
                            </div>
                            <svg
                              className="w-5 h-5 text-zinc-400 group-hover:text-indigo-500 transition-colors"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </div>
                        </Link>
                      )}

                      {/* Run URL */}
                      {msg.run_url && (
                        <a
                          href={msg.run_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg
                                   bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300
                                   hover:bg-indigo-100 dark:hover:bg-indigo-900/50
                                   text-sm font-medium transition-colors"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M14 5l7 7m0 0l-7 7m7-7H3"
                            />
                          </svg>
                          View workflow run
                        </a>
                      )}

                      {/* Quick reply options */}
                      {msg.options && msg.options.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-2">
                          {msg.options.map((opt) => (
                            <button
                              key={opt}
                              onClick={() => void sendMessage(opt)}
                              disabled={loading}
                              className="px-3 py-1.5 text-sm rounded-lg font-medium
                                       bg-zinc-100 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-300
                                       hover:bg-indigo-100 dark:hover:bg-indigo-900/30
                                       hover:text-indigo-700 dark:hover:text-indigo-300
                                       transition-colors disabled:opacity-50"
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Retry button for errors */}
                      {msg.error && i > 0 && messages[i - 1]?.role === 'user' && (
                        <button
                          onClick={() => {
                            const lastUserMsg = messages[i - 1].content;
                            setMessages(messages.slice(0, i - 1));
                            void sendMessage(lastUserMsg);
                          }}
                          disabled={loading}
                          className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg font-medium
                                   bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300
                                   hover:bg-red-200 dark:hover:bg-red-900/50
                                   transition-colors disabled:opacity-50"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                            />
                          </svg>
                          Retry
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Loading indicator */}
            <AnimatePresence>
              {loading && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex justify-start"
                >
                  <div className="relative">
                    <div className="absolute -left-10 top-0 w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/25">
                      <svg
                        className="w-4 h-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                    </div>
                    <div className="bg-white dark:bg-zinc-800/80 rounded-2xl px-5 py-4 shadow-sm border border-zinc-100 dark:border-zinc-700/50">
                      <div className="flex items-center gap-2">
                        <motion.div
                          className="w-2 h-2 bg-indigo-500 rounded-full"
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ repeat: Infinity, duration: 1, delay: 0 }}
                        />
                        <motion.div
                          className="w-2 h-2 bg-indigo-500 rounded-full"
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ repeat: Infinity, duration: 1, delay: 0.2 }}
                        />
                        <motion.div
                          className="w-2 h-2 bg-indigo-500 rounded-full"
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ repeat: Infinity, duration: 1, delay: 0.4 }}
                        />
                        <span className="ml-2 text-sm text-zinc-400">Thinking...</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input */}
        <div className="relative px-4 pb-4 pt-2">
          <div className="max-w-3xl mx-auto">
            <form
              onSubmit={handleSubmit}
              className="relative bg-white dark:bg-zinc-800 rounded-2xl shadow-lg shadow-zinc-200/50 dark:shadow-zinc-900/50 border border-zinc-200/50 dark:border-zinc-700/50"
            >
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about workflows..."
                disabled={loading}
                rows={1}
                className="w-full px-4 py-3.5 pr-14 bg-transparent text-zinc-900 dark:text-zinc-100
                         placeholder-zinc-400 dark:placeholder-zinc-500
                         focus:outline-none disabled:opacity-50 resize-none
                         text-[15px] leading-relaxed"
                style={{ maxHeight: '200px' }}
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="absolute right-2 bottom-2 p-2.5 rounded-xl
                         bg-gradient-to-r from-indigo-500 to-violet-500 text-white
                         hover:from-indigo-600 hover:to-violet-600
                         focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                         disabled:opacity-40 disabled:cursor-not-allowed
                         transition-all duration-200 shadow-lg shadow-indigo-500/25"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                  />
                </svg>
              </button>
            </form>
            <p className="text-center text-xs text-zinc-400 dark:text-zinc-500 mt-2">
              Studio Chat can make mistakes. Please double-check responses.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
